import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown, ChevronRight, FolderPlus, MoreHorizontal, Pencil, Trash2,
  Folder, FolderOpen,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { DocumentFolderTemplate } from "@/hooks/use-document-folder-templates";
import { useTemplateTreeContext } from "./TemplateTreeContext";
import { NewTemplateInput } from "./NewTemplateInput";

interface TemplateNodeProps {
  template: DocumentFolderTemplate;
  depth: number;
}

export function TemplateNode({ template, depth }: TemplateNodeProps) {
  const ctx = useTemplateTreeContext();
  const isExpanded = ctx.expandedIds.has(template.id);
  const isRenaming = ctx.renamingId === template.id;
  const children = ctx.childTemplatesByParent.get(template.id) ?? [];
  const indent = depth * 20;

  return (
    <div>
      {/* Template header row */}
      <div
        className={`flex items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 group transition-colors ${!template.isActive ? "opacity-50" : ""}`}
        style={{ paddingLeft: `${indent + 8}px` }}
      >
        <button
          className="shrink-0 flex items-center gap-0.5 p-0.5 rounded hover:bg-muted"
          onClick={() => ctx.toggleExpanded(template.id)}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? "Collapse" : "Expand"} ${template.name}`}
        >
          {isExpanded
            ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          }
          {isExpanded
            ? <FolderOpen className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            : <Folder className="h-4 w-4 text-amber-600 dark:text-amber-500" />
          }
        </button>

        {isRenaming ? (
          <Input
            autoFocus
            className="h-7 text-sm flex-1 max-w-xs"
            value={ctx.renameValue}
            onChange={e => ctx.setRenameValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") e.currentTarget.blur();
              if (e.key === "Escape") { ctx.onRenameCancel(); e.currentTarget.blur(); }
            }}
            onBlur={() => { if (ctx.renamingId === template.id) ctx.onRenameSubmit(); }}
          />
        ) : (
          <>
            <button
              className="flex-1 min-w-0 overflow-hidden text-left"
              onClick={() => ctx.toggleExpanded(template.id)}
            >
              <span className="text-sm font-medium block truncate">{template.name}</span>
            </button>
            {!template.isActive && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">inactive</Badge>
            )}
            {children.length > 0 && (
              <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{children.length}</span>
            )}
          </>
        )}

        {ctx.isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => ctx.onStartCreate(template.id)}>
                <FolderPlus className="h-3.5 w-3.5 mr-2" />Add Subfolder
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => ctx.onStartRename(template.id, template.name)}>
                <Pencil className="h-3.5 w-3.5 mr-2" />Rename
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => ctx.onDelete(template.id, template.name)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Expanded children */}
      {isExpanded && (
        <div>
          {/* New child input */}
          {ctx.creatingInParent === template.id && (
            <NewTemplateInput
              value={ctx.newName}
              onChange={ctx.setNewName}
              onSubmit={() => ctx.onCreateSubmit(template.id)}
              onCancel={ctx.onCreateCancel}
              depth={depth + 1}
              isCreating={ctx.isCreating}
            />
          )}

          {/* Child templates (recurse) */}
          {children.map(child => (
            <TemplateNode key={child.id} template={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}
